import {
  defaultSubtitleEditorData,
  defaultSubtitleEditorLogoData,
  type SubtitleEditorData,
  type SubtitleEditorLogoData,
} from '@/components/SubtitleEditor';
import { parsePaletteColors } from '@/lib/color/parsePaletteColors';
import {
  DEFAULT_SUBTITLE_TEXT_TRANSFORM,
  readKitSubtitleTextTransform,
} from '@/lib/subtitles/subtitleTextTransform';
import { parsePersistedPostLayout } from '@/lib/layouts/persistPostLayout';
import type { LayoutFieldBinds } from '@/lib/layouts/layoutFieldBinds';
import type { PostLayoutConfig } from '@/lib/layouts/types';
import { subtitleOutlineFromKit } from '@/lib/subtitles/subtitleOutline';

export interface BrandKitWorkspaceState {
  subtitle: SubtitleEditorData;
  logo: SubtitleEditorLogoData;
  fontId: string;
  postLayout: PostLayoutConfig | null;
  fieldBinds: LayoutFieldBinds;
  colors: string[];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function getPositionFromAlignItems(alignItems: string, justifyContent: string): string {
  const vMap: Record<string, string> = {
    'flex-start': 'top',
    center: 'middle',
    'flex-end': 'bottom',
  };
  const hMap: Record<string, string> = {
    'flex-start': 'left',
    center: 'center',
    'flex-end': 'right',
  };
  return `${vMap[alignItems] || 'bottom'}-${hMap[justifyContent] || 'center'}`;
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Kit `data` blob → editor workspace state. `loadBrandKitIntoEditor` applies
 *  subtitle/logo/fontId from this helper; postLayout is the full `{kind, ...config}`
 *  snapshot for the template create/edit preview. */
export function hydrateBrandKitWorkspace(data: unknown): BrandKitWorkspaceState {
  const record = asRecord(data) ?? {};
  const sub = asRecord(record.subtitle) ?? {};
  const logo = asRecord(record.logo) ?? {};
  const textAlign = sub.text_align;
  const colors = parsePaletteColors(record.colors);
  const parsedLayout = parsePersistedPostLayout(record.post_layout);

  return {
    fontId: asString(sub.subtitle_font_id, ''),
    colors,
    postLayout: parsedLayout?.postLayout ?? null,
    fieldBinds: parsedLayout?.fieldBinds ?? {},
    subtitle: {
      fontFamily: asString(sub.subtitle_font_id, defaultSubtitleEditorData.fontFamily),
      fontSize: asNumber(sub.subtitle_font_size, defaultSubtitleEditorData.fontSize),
      textColor: asString(sub.subtitle_color, defaultSubtitleEditorData.textColor),
      highlightColor: asString(
        sub.subtitle_highlight_color,
        defaultSubtitleEditorData.highlightColor,
      ),
      highlightEnabled: asBoolean(
        sub.subtitle_highlight_enabled,
        defaultSubtitleEditorData.highlightEnabled,
      ),
      highlightBoxEnabled: asBoolean(
        sub.highlight_box_enabled,
        defaultSubtitleEditorData.highlightBoxEnabled,
      ),
      highlightBoxBackgroundColor: asString(
        sub.highlight_box_background_color,
        defaultSubtitleEditorData.highlightBoxBackgroundColor,
      ),
      highlightBoxBorderRadius: asNumber(
        sub.highlight_box_border_radius,
        defaultSubtitleEditorData.highlightBoxBorderRadius,
      ),
      highlightBoxPaddingX: asNumber(
        sub.highlight_box_padding_x,
        defaultSubtitleEditorData.highlightBoxPaddingX,
      ),
      highlightBoxPaddingY: asNumber(
        sub.highlight_box_padding_y,
        defaultSubtitleEditorData.highlightBoxPaddingY,
      ),
      backgroundColor: asString(sub.background_color, defaultSubtitleEditorData.backgroundColor),
      backgroundEnabled: asBoolean(
        sub.background_enabled,
        defaultSubtitleEditorData.backgroundEnabled,
      ),
      backgroundOpacity: asNumber(
        sub.background_opacity,
        defaultSubtitleEditorData.backgroundOpacity,
      ),
      backgroundWidth: asNumber(sub.background_width, defaultSubtitleEditorData.backgroundWidth),
      backgroundHeight: asNumber(
        sub.background_height,
        defaultSubtitleEditorData.backgroundHeight,
      ),
      position: getPositionFromAlignItems(
        asString(sub.align_items, 'flex-end'),
        asString(sub.justify_content, 'center'),
      ),
      marginVertical: asNumber(
        sub.vertical_margin_percentage,
        defaultSubtitleEditorData.marginVertical,
      ),
      marginHorizontal: asNumber(
        sub.horizontal_margin_percentage,
        defaultSubtitleEditorData.marginHorizontal,
      ),
      shadowEnabled: asBoolean(sub.shadow_enabled, defaultSubtitleEditorData.shadowEnabled),
      shadowColor: asString(sub.shadow_color, defaultSubtitleEditorData.shadowColor),
      shadowSize: asNumber(sub.shadow_size, defaultSubtitleEditorData.shadowSize),
      ...subtitleOutlineFromKit(sub),
      animation: asString(sub.animation, defaultSubtitleEditorData.animation),
      textAlign:
        textAlign === 'left' || textAlign === 'center' || textAlign === 'right'
          ? textAlign
          : defaultSubtitleEditorData.textAlign,
      textTransform: readKitSubtitleTextTransform(sub, DEFAULT_SUBTITLE_TEXT_TRANSFORM),
      removePunctuation: asBoolean(sub.remove_punctuation, defaultSubtitleEditorData.removePunctuation),
      maxWordsPerLine: asNumber(
        sub.max_words_per_line,
        defaultSubtitleEditorData.maxWordsPerLine,
      ),
    },
    logo: {
      url: asString(logo.logo_image_url, '') || null,
      position: asString(logo.logo_position, defaultSubtitleEditorLogoData.position),
      size: asNumber(logo.logo_size, defaultSubtitleEditorLogoData.size),
      opacity: asNumber(logo.logo_opacity, defaultSubtitleEditorLogoData.opacity),
      horizontalMargin: asNumber(
        logo.logo_horizontal_margin,
        defaultSubtitleEditorLogoData.horizontalMargin,
      ),
      verticalMargin: asNumber(
        logo.logo_vertical_margin,
        defaultSubtitleEditorLogoData.verticalMargin,
      ),
    },
  };
}
